<template>
  <div>
    <div class="expiry-data d-flex justify-content-between align-items-center flex-wrap mb-3">
      <div class="d-flex align-items-center p-0">
        <h2 class="text-left mb-0">{{ record.unit_no || "-" }}</h2>
        <span
          v-if="record.safety_status"
          class="dvir-status ml-3"
          :class="safetyStatusClass"
        >
          {{ record.safety_status }}
        </span>
      </div>
      <b-button
        variant="primary"
        class="download-btn d-flex justify-content-center align-items-center px-3"
        :title="$t('dvir.downloadPdf')"
        @click="downloadPDF"
      >
        <svg class="mr-2" width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.96888 4.8302H7.93495V6.0715H9.96888C10.5737 6.0715 11.043 6.38927 11.043 6.66277V12.1671C11.043 12.4406 10.5737 12.7584 9.96888 12.7584H2.28092C1.6761 12.7584 1.20684 12.4406 1.20684 12.1671V6.66298C1.20684 6.38948 1.6761 6.07171 2.28092 6.07171H4.31444V4.83041H2.28092C1.00188 4.83041 0 5.63539 0 6.66298V12.1673C0 13.1951 1.00188 13.9999 2.28092 13.9999H9.96908C11.2479 13.9999 12.25 13.1949 12.25 12.1673V6.66298C12.2498 5.63518 11.2479 4.8302 9.96888 4.8302Z" fill="white"/>
          <path d="M4.33922 3.09394C4.49369 3.09394 4.64796 3.03332 4.76583 2.91209L5.52131 2.13504V4.83031V6.07161V8.44953C5.52131 8.79233 5.79144 9.07018 6.12473 9.07018C6.45802 9.07018 6.72815 8.79233 6.72815 8.44953V6.07161V4.83031V2.09304L7.52446 2.91209C7.64233 3.03332 7.7968 3.09394 7.95108 3.09394C8.10535 3.09394 8.25982 3.03332 8.37769 2.91209C8.61343 2.66983 8.61343 2.27675 8.37769 2.03449L6.57609 0.181437C6.45822 0.060203 6.30395 0 6.14967 0C6.14806 0 6.14665 0 6.14505 0C6.14344 0 6.14203 0 6.14042 0C5.98614 0 5.83187 0.060203 5.714 0.181437L3.9124 2.03449C3.67666 2.27675 3.67666 2.66983 3.9124 2.91209C4.03047 3.03332 4.18474 3.09394 4.33922 3.09394Z" fill="white"/>
        </svg>
        <p class="mb-0 text-white p-0 f-14">{{ $t("dvir.download") }}</p>
      </b-button>
    </div>

    <div class="setting-card">
      <!-- ── Details ── -->
      <h3 class="section-title text-left pl-0">{{ $t("dvir.detailsSection") }}</h3>
      <b-row class="mx-0 pl-0">
        <b-col md="6" class="px-0 text-left" v-for="(column, index) in detailColumns" :key="index">
          <div class="d-flex detail-row" v-for="field in column" :key="field.label">
            <p class="detail-label f-12 font-weight-bold text-black mb-0">{{ field.label }}</p>
            <p class="f-12 text-black mb-0">{{ field.value || "-" }}</p>
          </div>
        </b-col>
      </b-row>

      <!-- ── Items: every item on the inspection, in order ── -->
      <h3 class="section-title text-left mt-3 pl-0 mb-0">{{ $t("dvir.itemsSection") }}</h3>
      <!-- The row's content is half width on desktop; the divider still runs the
           full width of the card. -->
      <div class="review-item" v-for="item in reviewItems" :key="item.id">
        <div class="review-item-row text-left d-flex align-items-start justify-content-between">
          <div class="pr-3">
            <p class="f-14 font-weight-bold text-black mb-0">{{ item.name }}</p>
            <p class="f-14 text-grey-2 mb-0 mt-1" v-if="item.description">{{ item.description }}</p>
            <p
              v-if="item.answer"
              class="f-14 font-weight-bold text-uppercase mb-0 mt-2"
              :class="item.answer === 'fail' ? 'answer-fail' : 'answer-pass'"
            >
              {{ item.answer === "fail" ? $t("dvir.fail") : $t("dvir.pass") }}
            </p>
            <div class="d-flex flex-wrap mt-3" v-if="item.photos.length">
              <img
                v-for="(photo, i) in item.photos"
                :key="i"
                :src="photo"
                class="item-photo mr-3 cursor-pointer"
                alt=""
                @click="openPhoto(photo)"
              />
            </div>
            <div class="mt-3" v-if="item.note">
              <p class="f-14 font-weight-bold text-black mb-1">{{ $t("dvir.noteLabel") }}:</p>
              <p class="f-14 text-grey-2 mb-0">{{ item.note }}</p>
            </div>
          </div>
          <!-- Only a defect can be marked fixed; saved in the background. -->
          <b-form-checkbox
            v-if="item.answer === 'fail'"
            class="f-14 text-black text-nowrap"
            :checked="isFixed(item.id)"
            @change="setFixed(item, $event)"
          >
            {{ $t("dvir.fixed") }}
          </b-form-checkbox>
        </div>
      </div>
      <p class="f-14 text-grey text-left mb-0" v-if="!reviewItems.length">
        {{ $t("dvir.noItems") }}
      </p>

      <!-- ── Driver signature ── -->
      <h3 class="section-title text-left mt-4 pl-0">{{ $t("dvir.driverSignature") }}</h3>
      <p class="f-12 text-black text-left mb-3 pl-0">{{ $t("dvir.signatureDeclaration") }}</p>
      <div class="text-left pl-0" v-if="driverSignature">
        <img :src="driverSignature" class="signature-image" alt="" />
        <p class="f-14 text-black mb-0 mt-3" v-if="signedOn">{{ signedOn }}</p>
      </div>
      <p class="f-14 text-grey text-left mb-0 pl-0" v-else>{{ $t("dvir.noSignature") }}</p>

      <!-- An unsafe report can be signed off as repaired, which the API stores
           as the "Resolved" safety status. -->
      <div class="d-flex align-items-center flex-wrap mt-4 pl-0" v-if="showDefectsResolved">
        <b-form-checkbox v-model="defectsResolved" class="f-14 text-black pl-0" :disabled="isResolved">
          {{ $t("dvir.defectsResolved") }}
        </b-form-checkbox>
      </div>

      <!-- ── Mechanic signature ── -->
      <template v-if="showDefectsResolved">
        <h3 class="section-title text-left pl-0">
          {{ $t("dvir.mechanicSignature") }}
          {{ defectsResolved ? $t("dvir.requiredLabel") : $t("dvir.optionalLabel") }}
        </h3>
        <p class="f-12 text-black text-left mb-3 pl-0">{{ $t("dvir.signatureDeclaration") }}</p>
        <!-- Already signed: just the signature and when it was given. -->
        <div class="text-left pl-0" v-if="mechanicSignature">
          <img :src="mechanicSignature" class="signature-image" alt="" />
          <p class="f-14 text-black mb-0 mt-3" v-if="mechanicSignedOn">{{ mechanicSignedOn }}</p>
        </div>
        <div class="text-left px-0" v-else>
          <SignaturePad ref="mechanicPad" v-model="mechanicDraft" hide-clear />
          <div class="d-flex align-items-center flex-wrap mt-3">
            <b-button
              variant="outline-primary"
              class="h-40px px-3 f-14 d-flex justify-content-center align-items-center"
              :disabled="!mechanicDraft"
              @click="clearMechanicSignature"
            >
              {{ $t("dvir.clearSignature") }}
            </b-button>
            <b-button
              variant="primary"
              class="mx-0 mb-0 h-40px w-100px m-0 ml-2"
              @click="saveMechanicSignature"
            >
              {{ $t("dvir.ok") }}
            </b-button>
          </div>
        </div>
      </template>

      <div class="d-flex justify-content-end align-items-center flex-wrap mt-4 p-0">
        <b-button
          variant="secondary"
          class="mx-0 h-40px w-100px d-flex justify-content-center align-items-center f-14"
          @click="$emit('close')"
        >
          {{ $t("maintenanceShared.close") }}
        </b-button>
        <!-- <b-button
          variant="primary"
          class="export equipment d-flex justify-content-center align-items-center w-100px h-40px"
          @click="save"
        >
          <p class="mb-0 text-white p-0">{{ $t("dvir.save") }}</p>
        </b-button> -->
      </div>
    </div>

    <!-- Item photo, zoomable with the same panzoom viewer the document modals use. -->
    <b-modal
      id="modal-dvir-photo"
      centered
      size="lg"
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :body-class="'px-3 pt-2'"
      :header-class="'align-items-center'"
      @hidden="resetZoom"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">{{ $t("dvir.photo") }}</h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <div class="photo-frame">
        <div id="dvir-panzoom-element">
          <img :src="activePhoto" class="w-100" alt="" />
        </div>
        <div class="d-flex align-items-center justify-content-center zoom-section">
          <b-icon @click="zoom(1)" icon="zoom-in" style="fill: white; cursor: pointer"></b-icon>
          <b-icon @click="zoom(-1)" class="ml-3" icon="zoom-out" style="fill: white; cursor: pointer"></b-icon>
        </div>
      </div>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button
            class="mx-0 h-40px w-100px m-0"
            variant="secondary"
            @click="$bvModal.hide('modal-dvir-photo')"
          >
            {{ $t("maintenanceShared.close") }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import * as moment from "moment-timezone";
import { mapActions, mapGetters } from "vuex";
import SignaturePad from "@/components/Maintenance/SignaturePad.vue";
import Panzoom from "@panzoom/panzoom";
export default {
  name: "ReviewDVIR",
  components: { SignaturePad },
  props: {
    inspection: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      inspectionData: null,
      defectsResolved: false,
      mechanicDraft: "",
      // Fixed flags live here so a tick can be rolled back if its save fails.
      fixedByItem: {},
      activePhoto: "",
      panzoom: null,
    };
  },
  computed: {
    ...mapGetters(["company"]),
    record() {
      return this.inspectionData || this.inspection || {};
    },
    // Two label/value columns, left and right, as laid out in the design.
    detailColumns() {
      return [
        [
          { label: this.$t("maintenanceShared.unitNo"), value: this.record.unit_no },
          { label: this.$t("dvir.unitType"), value: this.record.unit_type || this.record.equipment_type },
          { label: this.$t("dvir.template"), value: this.record.template_name },
          { label: this.$t("dvir.createdBy"), value: this.createdByName },
        ],
        [
          { label: this.$t("dvir.dateTime"), value: this.formatDate(this.record.inspected_at) },
          { label: this.$t("dvir.odometer"), value: this.record.odometer },
          { label: this.$t("dvir.location"), value: this.record.location },
          { label: this.$t("dvir.company"), value: this.companyName },
        ],
      ];
    },
    // The list row carries created_by as a name, the detail API as an object.
    createdByName() {
      const createdBy = this.record.created_by;
      if (!createdBy) return "";
      return typeof createdBy === "string" ? createdBy : createdBy.name;
    },
    companyName() {
      return (this.company || [])[0]?.name || "";
    },
    // Every item on the inspection, in the order the driver saw them.
    reviewItems() {
      return [...(this.record.items || [])]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          note: item.note,
          answer: item.answer,
          photos: item.photos || [],
        }));
    },
    driverSignature() {
      return (this.record.signatures || {}).driver || "";
    },
    mechanicSignature() {
      return (this.record.signatures || {}).mechanic || "";
    },
    mechanicSignedOn() {
      const signedAt = (this.record.signatures || {}).mechanic_signed_at;
      return signedAt ? moment(signedAt).format("MM/DD/YYYY") : "";
    },
    isResolved() {
      return this.record.safety_status === "Resolved";
    },
    // Only a report that flagged the unit unsafe has defects to resolve.
    showDefectsResolved() {
      return this.record.defect_count > 0
    },
    // Stamped when the driver signed; older records fall back to the completion time.
    signedOn() {
      const signatures = this.record.signatures || {};
      const signedAt = signatures.driver_signed_at || this.record.completed_at || this.record.inspected_at;
      return signedAt ? moment(signedAt).format("MM/DD/YYYY") : "";
    },
    safetyStatusClass() {
      return `${this.record.safety_status}`.toLowerCase() === "unsafe"
        ? "dvir-status-unsafe" : `${this.record.safety_status}`.toLowerCase() === "safe"
         ? "dvir-status-safe": "dvir-status-resolved"
    },
  },
  methods: {
    ...mapActions([
      "getDVIRInspection",
      "getDVIRInspectionPDF",
      "reviewDVIRInspection",
      "updateDVIRInspection",
      "setNotification",
      "setLoader",
    ]),
    formatDate(date) {
      return date ? moment(date).format("MM/DD/YYYY HH:mm") : "";
    },
    openPhoto(url) {
      this.activePhoto = url;
      this.$bvModal.show("modal-dvir-photo");
    },
    // Same viewer as the document modals: panzoom is created on first zoom and
    // dropped when the modal closes so the next photo starts unscaled.
    zoom(level) {
      if (!this.panzoom) {
        this.panzoom = Panzoom(document.getElementById("dvir-panzoom-element"), {
          maxScale: 5,
        });
      }
      level === -1 ? this.panzoom.zoomOut() : this.panzoom.zoomIn();
    },
    resetZoom() {
      if (this.panzoom) this.panzoom.destroy();
      this.panzoom = null;
      this.activePhoto = "";
    },
    async loadInspection() {
      if (!this.inspection || !this.inspection.id) return;
      try {
        this.setLoader(true);
        this.inspectionData = await this.getDVIRInspection(this.inspection.id);
        // An already resolved report comes back with the box ticked.
        this.defectsResolved = this.isResolved;
        this.mechanicDraft = "";
        this.syncFixed();
      } catch (error) {
        console.error("Error fetching DVIR inspection:", error);
      } finally {
        this.setLoader(false);
      }
    },
    clearMechanicSignature() {
      if (this.$refs.mechanicPad) this.$refs.mechanicPad.clear();
    },
    // The API replaces `result` wholesale on PATCH, so the answers have to go
    // back up with whatever else is being saved.
    resultItems() {
      return (this.record.items || []).map((item) => {
        // Photos are left out: the API merges uploaded media in by item_ref.
        const entry = { id: item.id, answer: item.answer || null, fixed: this.isFixed(item.id) };
        if (item.note) entry.note = item.note;
        return entry;
      });
    },
    resultPayload(signatures, safetyStatus) {
      const result = { items: this.resultItems(), signatures };
      const payload = { result };
      if (safetyStatus) {
        result.safety_status = safetyStatus;
        payload.safety_status = safetyStatus;
      }
      return payload;
    },
    syncFixed() {
      this.fixedByItem = (this.record.items || []).reduce(
        (flags, item) => ({ ...flags, [item.id]: !!item.fixed }),
        {}
      );
    },
    isFixed(id) {
      return !!this.fixedByItem[id];
    },
    // Saved as soon as it is ticked — no loader, it is a one-field change. The
    // answer stays as the driver left it; only the fixed flag moves.
    async setFixed(item, checked) {
      this.$set(this.fixedByItem, item.id, checked);
      try {
        await this.updateDVIRInspection({
          id: this.record.id,
          payload: this.resultPayload(this.record.signatures || {}, this.record.safety_status),
        });
      } catch (error) {
        this.$set(this.fixedByItem, item.id, !checked);
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      }
    },
    async saveMechanicSignature() {
      if (!this.mechanicDraft) {
        await this.setNotification({
          msg: this.$t("dvir.signatureRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      try {
        this.setLoader(true);
        // The whole signature object goes back, with the mechanic's added.
        const signatures = {
          ...(this.record.signatures || {}),
          mechanic: this.mechanicDraft,
          mechanic_signed_at: new Date().toISOString(),
        };
        // Only a ticked box closes the defects out; otherwise the report keeps
        // the status the driver gave it.
        const safetyStatus = this.defectsResolved ? "Resolved" : this.record.safety_status;
        await this.updateDVIRInspection({
          id: this.record.id,
          payload: this.resultPayload(signatures, safetyStatus),
        });
        await this.setNotification({
          msg: this.$t("dvir.dvirSaved"),
          type: "success",
          color: "green",
        });
        await this.loadInspection();
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    // unit_date_template.pdf — slashes and the like can't go in a filename.
    pdfFileName() {
      const clean = (value) =>
        `${value || ""}`.trim().replace(/[\\/:*?"<>|]/g, "-") || "-";
      const date = this.record.inspected_at
        ? moment(this.record.inspected_at).format("MM-DD-YYYY")
        : "";
      return `${clean(this.record.unit_no)}_${date}_${clean(this.record.template_name)}.pdf`;
    },
    async downloadPDF() {
      if (!this.record.id) return;
      try {
        this.setLoader(true);
        const response = await this.getDVIRInspectionPDF(this.record.id);
        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/pdf",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = this.pdfFileName();
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    async save() {
      try {
        this.setLoader(true);
        // Review is its own endpoint — it stamps reviewed_by/reviewed_at server
        // side, so nothing from the report itself needs to be sent back.
        await this.reviewDVIRInspection({
          id: this.record.id,
          payload: { status: "Reviewed", condition_satisfactory: true },
        });
        await this.setNotification({
          msg: this.$t("dvir.dvirSaved"),
          type: "success",
          color: "green",
        });
        this.$emit("close");
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error?.error) return error.error;
      if (error?.detail) return error.detail.error || error.detail;
      const firstField = error && Object.values(error)[0];
      if (Array.isArray(firstField)) return firstField[0];
      return firstField || this.$t("dvir.dvirSaveFailed");
    },
  },
  created() {
    // Seed from the row we were handed so the checkboxes render right away;
    // the fetch refreshes them.
    this.syncFixed();
    this.loadInspection();
  },
};
</script>
<style scoped>
.setting-card {
  padding: 1.5rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.section-title {
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e3e3e3;
  font-size: 18px;
  font-weight: 600;
  color: #000000;
}
.text-red {
  color: #ff0000;
}
.detail-row {
  margin-bottom: 0.75rem;
}
.detail-label {
  flex: 0 0 170px;
  max-width: 170px;
  padding-right: 0.75rem;
}
.review-item {
  padding: 1.5rem 0;
  border-bottom: 1px solid #e3e3e3;
}
.review-item:last-of-type {
  padding-bottom: 0;
  border-bottom: none;
}
/* Content is half width on desktop, full width on mobile — the divider above
   always spans the whole card. */
.review-item-row {
  width: 100%;
}
@media (min-width: 768px) {
  .review-item-row {
    width: 50%;
  }
}
.answer-pass {
  color: #2cce89;
}
.answer-fail {
  color: #ff0000;
}
.item-photo {
  width: 180px;
  max-width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}
/* Holds the zoomed image; the bar below it stays put while the image pans. */
.photo-frame {
  max-height: 70vh;
  overflow: auto;
}
.photo-frame #dvir-panzoom-element {
  overflow: hidden;
}
.signature-image {
  display: block;
  max-width: 280px;
  max-height: 76px;
  object-fit: contain;
}
.download-btn {
  width: 188px;
  height: 40px;
  background: #2CCE89 !important;
  color: white !important;
}
.dvir-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 30px;
  border-radius: 4px;
  color: #ffffff;
  font-size: 14px;
  text-transform: uppercase;
}
.dvir-status-safe {
  background-color: #2cce89;
}
.dvir-status-unsafe {
  background-color: #ff0000;
}
.dvir-status-resolved {
  background-color: #FFC800;
}

</style>
